import { DEFAULT_LOCALE, type SupportedLocale } from "@/i18n";

interface LinkHref {
  href: string;
}

/** Locale-keyed destinations shared by header (home) and footer (legal). */
interface LocaleSiteLinks {
  home: LinkHref;
  customerService: LinkHref;
  legalNotice: LinkHref;
  dataProtection: LinkHref;
}

export type FooterLinks = Omit<LocaleSiteLinks, "home">;

/** Global Fielmann homepage and fallback for unsupported locales. */
const FIELMANN_EU_HOME_LINK: LinkHref = {
  href: "https://www.fielmann.eu/",
};

/**
 * Functional link destinations per locale, keyed independently from translated
 * labels. Header and footer each read the fields they display.
 */
export const SITE_LINKS_BY_LOCALE = {
  "cs-CZ": {
    home: { href: "https://www.fielmann.cz/" },
    customerService: {
      href: "https://zakaznickyservis.fielmann.cz/hc/cs/requests/new",
    },
    legalNotice: { href: "https://www.fielmann.cz/impresum/" },
    dataProtection: { href: "https://www.fielmann.cz/ochrana-udaju/" },
  },
  "de-AT": {
    home: { href: "https://www.fielmann.at/" },
    customerService: {
      href: "https://kundenservice.fielmann.at/hc/de-at/requests/new?ticket_form_id=360004068117",
    },
    legalNotice: { href: "https://www.fielmann.at/impressum/" },
    dataProtection: { href: "https://www.fielmann.at/datenschutz/" },
  },
  "de-CH": {
    home: { href: "https://www.fielmann.ch/" },
    customerService: {
      href: "https://service.fielmann.ch/hc/de-ch/requests/new/",
    },
    legalNotice: { href: "https://www.fielmann.ch/impressum/" },
    dataProtection: { href: "https://www.fielmann.ch/datenschutz/" },
  },
  "de-DE": {
    home: { href: "https://www.fielmann.de/" },
    customerService: {
      href: "https://kundenservice.fielmann.de/hc/de/requests/new?ticket_form_id=23238753878929",
    },
    legalNotice: { href: "https://www.fielmann.de/impressum/" },
    dataProtection: { href: "https://www.fielmann.de/datenschutz/" },
  },
  "de-IT": {
    home: { href: "https://www.fielmann.it/" },
    customerService: { href: "https://service.fielmann.it/#todo" },
    legalNotice: { href: "https://www.fielmann.it/#todo" },
    dataProtection: { href: "https://www.fielmann.it/#todo" },
  },
  "de-LU": {
    home: { href: "https://www.fielmann.lu/" },
    customerService: { href: "https://service.fielmann.lu/#todo" },
    legalNotice: { href: "https://www.fielmann.lu/#todo" },
    dataProtection: { href: "https://www.fielmann.lu/#todo" },
  },
  "fr-CH": {
    home: { href: "https://fr.fielmann.ch/" },
    customerService: {
      href: "https://service.fielmann.ch/hc/fr-ch/requests/new/",
    },
    legalNotice: { href: "https://fr.fielmann.ch/mentions-legales/" },
    dataProtection: {
      href: "https://fr.fielmann.ch/protectiondesdonnees/",
    },
  },
  "fr-LU": {
    home: { href: "https://www.fielmann.lu/" },
    customerService: { href: "https://service.fielmann.lu/#todo" },
    legalNotice: { href: "https://fr.fielmann.lu/#todo" },
    dataProtection: { href: "https://fr.fielmann.lu/#todo" },
  },
  "it-CH": {
    home: { href: "https://it.fielmann.ch/" },
    customerService: {
      href: "https://service.fielmann.ch/hc/it-ch/requests/new/",
    },
    legalNotice: { href: "https://it.fielmann.ch/datisocietari/" },
    dataProtection: { href: "https://it.fielmann.ch/protezionedeidati/" },
  },
  "it-IT": {
    home: { href: "https://www.fielmann.it/" },
    customerService: { href: "https://service.fielmann.it/#todo" },
    legalNotice: { href: "https://it.fielmann.it/#todo" },
    dataProtection: { href: "https://it.fielmann.it/#todo" },
  },
  "lb-LU": {
    home: { href: "https://www.fielmann.lu/" },
    customerService: { href: "https://service.fielmann.lu/#todo" },
    legalNotice: { href: "https://www.fielmann.lu/#todo" },
    dataProtection: { href: "https://www.fielmann.lu/#todo" },
  },
  "pl-PL": {
    home: { href: "https://www.fielmann.pl/" },
    customerService: {
      href: "https://obslugaklienta.fielmann.pl/hc/pl/requests/new",
    },
    legalNotice: { href: "https://www.fielmann.pl/impressum/" },
    dataProtection: { href: "https://www.fielmann.pl/ochronadanych/" },
  },
} satisfies Record<SupportedLocale, LocaleSiteLinks>;

/** Returns footer legal destinations, falling back defensively to de-DE. */
export function getFooterLinks(locale: string): FooterLinks {
  const links =
    locale in SITE_LINKS_BY_LOCALE
      ? SITE_LINKS_BY_LOCALE[locale as SupportedLocale]
      : SITE_LINKS_BY_LOCALE[DEFAULT_LOCALE];

  return {
    customerService: links.customerService,
    legalNotice: links.legalNotice,
    dataProtection: links.dataProtection,
  };
}

/** Returns the locale homepage, falling back defensively to fielmann.eu. */
export function getHomeLink(locale: string): LinkHref {
  return locale in SITE_LINKS_BY_LOCALE
    ? SITE_LINKS_BY_LOCALE[locale as SupportedLocale].home
    : FIELMANN_EU_HOME_LINK;
}

/** Site-wide non-localized content for the auth screens. */
export const SITE_LINKS = {
  copyright: "© Fielmann Group AG",
} as const;
